import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ApexNonAxisChartSeries,
  ApexChart,
  ApexTooltip,
  ApexDataLabels,
  ApexPlotOptions,
  ApexFill,
  ApexStroke,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexTitleSubtitle,
  ApexLegend,
  ApexMarkers,
  ApexGrid,
  ApexStates,
  ApexAnnotations,
  ChartComponent,
  ApexResponsive,
} from 'ng-apexcharts';
import { forkJoin } from 'rxjs';
import { DatePipe, DecimalPipe } from '@angular/common';
import { ContractAPIService } from 'src/app/extensions/services/api/contracts-api.service';
import { InvoiceAPIService } from 'src/app/extensions/services/api/invoice-api.service';
import { PaymentAPIService } from 'src/app/extensions/services/api/payment-api.service';
import { Router } from '@angular/router';
import { isNotEmpty, isSafari } from 'src/app/extensions/utilities';
import { DictionaryContractType } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { TIMEFRAME } from 'src/app/extensions/constants/commonConstants';

type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  fill: ApexFill;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  labels: any[];
  states: ApexStates;
  responsive: ApexResponsive[];
};

type GraphOptions = {
  fill: ApexFill;
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  title: ApexTitleSubtitle;
  labels: string[];
  legend: ApexLegend;
  subtitle: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  markers: ApexMarkers;
  grid: ApexGrid;
  annotations: ApexAnnotations;
  responsive: ApexResponsive[];
};

@Component({
  selector: 'dashboard-contract',
  templateUrl: './dashboard-contract.component.html',
  styleUrls: ['./dashboard-contract.component.scss'],
  standalone: false,
})
export class DashboardContractComponent implements OnInit {
  @ViewChild('chartPayments') chartPayments: ChartComponent;
  @ViewChild('chartContract') chartContract: ChartComponent;

  ContractType = DictionaryContractType;
  chartOptions: Partial<ChartOptions>;
  graphOptions: Partial<GraphOptions>;

  public constructor(
    private _translateService: TranslateService,
    private _contractAPIService: ContractAPIService,
    private _invoiceAPIService: InvoiceAPIService,
    private _paymentAPIService: PaymentAPIService,
    private _datePipe: DatePipe,
    private _decimalPipe: DecimalPipe,
    private _router: Router
  ) {}

  public otherTimeframePaymentTooltip: ApexTooltip = {
    custom: ({ series, seriesIndex, dataPointIndex }) => {
      return this.getPaymentTooltip(
        this.startingMonth > 0 ? TIMEFRAME.FUTURE : TIMEFRAME.PAST,
        this.paymentsData.monthlyPayments,
        series,
        seriesIndex,
        dataPointIndex
      );
    },
  };

  public currentPaymentTooltip: ApexTooltip = {
    custom: ({ series, seriesIndex, dataPointIndex }) => {
      if (seriesIndex === 0) {
        const monthlyPayments = this.getMonthlyPaymentsByTimeframe(TIMEFRAME.PAST);
        return this.getPaymentTooltip(TIMEFRAME.PAST, monthlyPayments, series, seriesIndex, dataPointIndex);
      }
      if (seriesIndex === 1) {
        const monthlyPayments = this.getMonthlyPaymentsByTimeframe(TIMEFRAME.FUTURE);
        return this.getPaymentTooltip(TIMEFRAME.FUTURE, monthlyPayments, series, seriesIndex, dataPointIndex);
      }
    },
  };

  public contract: app.models.dashboard.ContractModel;
  public contracts: app.models.dashboard.ContractModel[] = [];
  public invoicesData: app.models.dashboard.InvoicesDataModel;
  public paymentsData: app.models.dashboard.PaymentsDataModel;
  public timeStep: number = 6;
  public pastColor: string = 'var(--dc-pc-past-color)';
  public futureColor: string = 'var(--dc-pc-future-color)';
  public currentMonthHighlight = new Array(this.timeStep).fill('').concat([this.pastColor]);
  public isNextSwitchEnabled: boolean = true;
  public isPrevSwitchEnabled: boolean = true;
  public startDate: Date;
  public endDate: Date;
  public startingMonth: number = 0;

  public chartDataLoaded = {
    CONTRACTS: false,
    INVOICES: false,
    PAYMENTS: false,
  };
  public chartHasData = {
    CONTRACTS: false,
    INVOICES: false,
    PAYMENTS: false,
  };

  public ngOnInit(): void {
    let today = new Date();
    this.startDate = new Date(today.getFullYear(), today.getMonth() - this.timeStep, 1);
    this.endDate = new Date(today.getFullYear(), today.getMonth() + this.timeStep + 1, 0);
    const source = forkJoin([
      this._contractAPIService.getDashboardContracts(),
      this._invoiceAPIService.getDashboardInvoicesData(),
      this._paymentAPIService.getDashboardPaymentsData(this.startDate, this.endDate),
    ]);
    source.subscribe(([contracts, invoicesData, paymentsData]) => {
      this.chartHasData.CONTRACTS = contracts.some(x => x);
      this.chartHasData.INVOICES = isNotEmpty(invoicesData);
      this.chartHasData.PAYMENTS = paymentsData.response?.monthlyPayments?.some(x => x) ?? false;
      this.chartDataLoaded.INVOICES = true;
      this.chartDataLoaded.PAYMENTS = true;
      this.chartDataLoaded.CONTRACTS = true;

      if (this.chartHasData.CONTRACTS) {
        this.contracts = contracts.map((x, i) => ({ ...x, index: i + 1 }));
        this.contract = this.contracts[0];
        this.updateContractChart();
      }

      if (this.chartHasData.INVOICES) {
        this.invoicesData = invoicesData;
      }

      if (this.chartHasData.PAYMENTS) {
        paymentsData.response.monthlyPayments?.forEach(x => {
          x.date = new Date(x.date);
          x.date.setDate(x.date.getDate() + 2);
        });
        this.paymentsData = paymentsData.response;
        this.initializePaymentChart();
      }
    });
  }

  public getPaymentTooltip(
    timeframe: TIMEFRAME,
    monthlyPayments: app.models.dashboard.MonthlyPaymentsModel[],
    series: any,
    seriesIndex: any,
    dataPointIndex: any
  ): string {
    let innerHtml = `<div class="graph-tooltip graph-tooltip-${timeframe}-box data-hj-suppress">`;
    innerHtml += `
      <div class="graph-tooltip-title-txt">${this._translateService.instant('dashboard.contracts')}</div>
      <div class="graph-tooltip-row-box">`;

    const details = monthlyPayments[dataPointIndex]?.details;
    details?.forEach(contract => {
      innerHtml += `<div class="graph-tooltip-row-txt">${contract},</div>`;
    });

    innerHtml += `</div>
      <div class="graph-tooltip-separator"></div>
      <div class="graph-tooltip-sum-box">
        ${this._decimalPipe.transform(series[seriesIndex][dataPointIndex], '1.2-2')} ${monthlyPayments[dataPointIndex]?.currency}
      </div>`;
    if (timeframe === TIMEFRAME.FUTURE) {
      innerHtml += `<div class="graph-tooltip-additional-amounts-txt">
        ${this._translateService.instant('dashboard.contractView.payment.additionalAmounts')}
      </div>`;
    }
    innerHtml += `</div>`;
    return innerHtml;
  }

  public getPastMonthlyAmounts() {
    const monthlyPayments = this.getMonthlyPaymentsByTimeframe(TIMEFRAME.PAST);
    return monthlyPayments.map(x => [x.date.getTime(), x.amount]);
  }

  public getFutureMonthlyAmounts() {
    const monthlyPayments = this.getMonthlyPaymentsByTimeframe(TIMEFRAME.FUTURE);
    return monthlyPayments.map(x => [x.date.getTime(), x.amount]);
  }

  public getMonthlyPaymentsByTimeframe(timeframe: TIMEFRAME) {
    if (
      this.paymentsData.monthlyPayments == null ||
      this.paymentsData.monthlyPayments == undefined ||
      !this.paymentsData.monthlyPayments.some(x => x)
    )
      return [];

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    let monthlyPayments: app.models.dashboard.MonthlyPaymentsModel[] = [];
    if (timeframe === TIMEFRAME.FUTURE) {
      monthlyPayments = this.paymentsData.monthlyPayments.filter(
        x =>
          x.date.getFullYear() > currentYear ||
          (x.date.getFullYear() === currentYear && x.date.getMonth() >= currentMonth)
      );
    } else {
      monthlyPayments = this.paymentsData.monthlyPayments.filter(
        x =>
          x.date.getFullYear() < currentYear ||
          (x.date.getFullYear() === currentYear && x.date.getMonth() <= currentMonth)
      );
    }

    return monthlyPayments;
  }

  initializePaymentChart() {
    this.graphOptions = {
      chart: {
        type: 'area',
        height: 420,
        zoom: {
          enabled: false,
        },
        foreColor: 'var(--dc-pc-chart-fore-color)',
        toolbar: {
          show: false,
        },
      },
      annotations: {
        xaxis: [
          {
            x: new Date().setDate(2),
            borderColor: 'var(--dc-pc-annotations-xaxis-border-color)',
            borderWidth: 3,
            strokeDashArray: 0,
          },
        ],
      },
      grid: {
        show: true,
        borderColor: 'var(--dc-pc-grid-border-color)',
        yaxis: {
          lines: {
            show: true,
          },
        },
        xaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          left: 30,
          right: 35,
          bottom: 20,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
      },
      xaxis: {
        type: 'datetime',
        tickAmount: 12,
        labels: {
          show: true,
          showDuplicates: true,
          style: {
            fontSize: '14px',
            fontWeight: '600',
            colors: this.currentMonthHighlight,
          },
          formatter: timestamp => this._datePipe.transform(new Date(timestamp), 'MMM'),
          hideOverlappingLabels: false,
          trim: true,
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '14px',
            fontWeight: '600',
            cssClass: 'data-hj-suppress',
          },
          formatter: x => this._decimalPipe.transform(x, '1.'),
        },
      },
      tooltip: this.currentPaymentTooltip,
      markers: {
        colors: ['var(--dc-pc-markers-colors-1)'],
        strokeColors: [this.pastColor, this.futureColor],
        strokeWidth: 3,
        hover: {
          size: 7.9,
        },
      },
      series: [
        {
          name: this._translateService.instant('dashboard.contractView.payment.paidAmount'),
          data: this.getPastMonthlyAmounts(),
          color: this.pastColor,
        },
        {
          name: this._translateService.instant('dashboard.contractView.payment.futurePayments'),
          data: this.getFutureMonthlyAmounts(),
          color: this.futureColor,
        },
      ],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.3,
          opacityFrom: 0.7,
          opacityTo: 0.0,
        },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: {
              width: '100%',
              height: 'auto',
            },
            annotations: {
              xaxis: [
                {
                  x: new Date().setDate(2),
                  borderColor: 'var(--dc-pc-annotations-xaxis-border-color)',
                  borderWidth: 2,
                  strokeDashArray: 0,
                },
              ],
            },
            grid: {
              padding: {
                left: 10,
                right: 10,
                bottom: 5,
              },
            },
            stroke: {
              width: 2,
            },
            xaxis: {
              labels: {
                rotateAlways: true,
                style: {
                  fontSize: '12px',
                  fontWeight: '500',
                },
              },
            },
            yaxis: {
              labels: {
                style: {
                  fontSize: '12px',
                },
              },
            },
            markers: {
              strokeWidth: 2,
            },
          },
        },
      ],
    } as GraphOptions;
  }

  public updatePaymentChart() {
    if (this.startingMonth == 0) {
      this.chartPayments.updateOptions({
        tooltip: this.currentPaymentTooltip,
        xaxis: {
          labels: {
            style: {
              colors: this.currentMonthHighlight,
            },
          },
        },
        markers: {
          strokeColors: [this.pastColor, this.futureColor],
        },
      });
      this.chartPayments.updateSeries([
        {
          name: this._translateService.instant('dashboard.contractView.payment.paidAmount'),
          data: this.getPastMonthlyAmounts(),
          color: this.pastColor,
        },
        {
          name: this._translateService.instant('dashboard.contractView.payment.futurePayments'),
          data: this.getFutureMonthlyAmounts(),
          color: this.futureColor,
        },
      ]);
    } else {
      this.chartPayments.updateOptions({
        labels: this.paymentsData?.monthlyPayments?.map(x => x.date.getTime()),
        tooltip: this.otherTimeframePaymentTooltip,
        markers: {
          strokeColors: [this.startingMonth < 0 ? this.pastColor : this.futureColor],
        },
        xaxis: {
          labels: {
            style: {
              colors: '',
            },
          },
        },
      });
      this.chartPayments.updateSeries([
        {
          data: this.paymentsData?.monthlyPayments?.map(x => x.amount) ?? [],
          color: this.startingMonth < 0 ? this.pastColor : this.futureColor,
        },
      ]);
    }
  }

  public loadOtherPayments() {
    this._paymentAPIService.getDashboardPaymentsData(this.startDate, this.endDate).subscribe(paymentsData => {
      paymentsData.response.monthlyPayments?.forEach(x => {
        x.date = new Date(x.date);
        x.date.setDate(x.date.getDate() + 2);
      });
      this.paymentsData = paymentsData.response;

      this.updatePaymentChart();
    });
  }

  public loadPrevMonths() {
    const monthsToGoBack = 2 * this.timeStep;
    this.startingMonth -= monthsToGoBack;

    this.isNextSwitchEnabled = true;
    if (this.startingMonth < 0) {
      this.isPrevSwitchEnabled = false;
    } else {
      this.isPrevSwitchEnabled = true;
    }

    this.startDate.setMonth(this.startDate.getMonth() - monthsToGoBack);
    this.endDate.setMonth(this.endDate.getMonth() - monthsToGoBack);

    this.loadOtherPayments();
  }

  public loadNextMonths() {
    const monthsToJump = 2 * this.timeStep;
    this.startingMonth += monthsToJump;

    this.isPrevSwitchEnabled = true;
    if (this.startingMonth > 0) {
      this.isNextSwitchEnabled = false;
    } else {
      this.isNextSwitchEnabled = true;
    }

    this.startDate.setMonth(this.startDate.getMonth() + monthsToJump);
    this.endDate.setMonth(this.endDate.getMonth() + monthsToJump);

    this.loadOtherPayments();
  }

  public updateContractChart() {
    let currentValue = this.contract;
    const contractTypePercentageChart = [DictionaryContractType.FINANCIAL_LEASING, DictionaryContractType.CREDIT];

    this.chartOptions = {
      series: [
        contractTypePercentageChart.includes(currentValue.dictionaryContractType.code)
          ? currentValue.paidPercentage
          : Math.round((currentValue.paidInstallmentsNumber / currentValue.totalInstallmentsNumber) * 100),
      ],
      chart: {
        height: 350,
        type: 'radialBar',
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 9,
          color: 'var(--dc-cc-chart-drop-shadow-color)',
          opacity: 0.5,
        },
      },
      plotOptions: {
        radialBar: {
          track: {
            background: 'var(--dc-cc-plot-options-radial-bar-track-background)',
          },
          dataLabels: {
            name: {
              offsetY: 20,
              color: 'var(--dc-cc-plot-options-radial-bar-data-labels-name-color)',
              fontSize: '20px',
              fontWeight: 'bold',
            },
            value: {
              offsetY: -20,
              color: 'var(--dc-cc-plot-options-radial-bar-data-labels-value-color)',
              fontSize: '36px',
              fontWeight: 'bold',
              formatter: function (val) {
                if (contractTypePercentageChart.includes(currentValue.dictionaryContractType.code)) {
                  return `${val}%`;
                } else {
                  return `${currentValue.paidInstallmentsNumber}/${currentValue.totalInstallmentsNumber}`;
                }
              },
            },
          },
        },
      },
      fill: {
        type: 'solid',
        colors: ['var(--dc-cc-fill-colors-1)'],
      },
      stroke: {
        lineCap: 'round',
      },
      tooltip: {
        enabled:
          this.contract.dictionaryContractType.code === DictionaryContractType.OPERATIONAL_LEASING ? false : true,
        followCursor: false,
        fixed: {
          enabled: true,
          offsetX: 15,
          offsetY: 20,
        },
        custom: () => `<div class="chart-tooltip-box">
          <div class="chart-tooltip-out data-hj-suppress"> ${this._decimalPipe.transform(this.contract.unpaidAmount, '1.2-2')} ${
            this.contract.currency
          } </div>
          <div class="chart-tooltip data-hj-suppress"> ${this._decimalPipe.transform(this.contract.paidAmount, '1.2-2')} ${
            this.contract.currency
          } </div>
        </div>`,
      },
      labels: [this._translateService.instant('dashboard.contractView.contract.paid')],
      states: {
        hover: {
          filter: {
            type: isSafari() ? 'none' : 'lighten',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      responsive: [
        {
          breakpoint: 576,
          options: {
            chart: {
              width: '100%',
              height: 'auto',
            },
          },
        },
      ],
    } as ChartOptions;
  }

  public contractHandler(index: number) {
    this.contract = this.contracts[index];

    this.updateContractChart();
  }

  public nextHandler() {
    this.contractHandler(this.contracts.indexOf(this.contract) + 1);
  }

  public previousHandler() {
    this.contractHandler(this.contracts.indexOf(this.contract) - 1);
  }

  public contractNavigateHandler() {
    this._router.navigate(['contracts', this.contract.contractId]);
  }

  public invoiceNavigateHandler() {
    this._router.navigate(['invoices'], { queryParams: { contractId: this.contract?.contractId } });
  }
}
